import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PublicIcon from "@mui/icons-material/Public";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ChatIcon from "@mui/icons-material/Chat";
import { getStatusChip } from "../../utils/utils";

const channelIconByType = {
  WhatsApp: <WhatsAppIcon fontSize="small" />,
  App: <PhoneIphoneIcon fontSize="small" />,
  Web: <PublicIcon fontSize="small" />,
  Site: <PublicIcon fontSize="small" />,
  Messenger: <ChatIcon fontSize="small" />,
};

const getChannelIcon = (type) => channelIconByType[type] ?? <PublicIcon fontSize="small" />;

const money = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const InfoPill = ({ label, value, highlight }) => (
  <Box
    sx={{
      border: 1,
      borderColor: "divider",
      bgcolor: highlight ? "primary.main" : "action.hover",
      color: highlight ? "primary.contrastText" : "text.primary",
      borderRadius: 1.5,
      px: 1.25,
      py: 0.85,
      flex: 1,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" sx={{ opacity: 0.85, display: "block", lineHeight: 1 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap title={value}>
      {value}
    </Typography>
  </Box>
);

const InfoRow = ({ icon, children }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Box sx={{ mt: 0.25, color: "text.secondary", flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
  </Stack>
);

export default function ClientPurchaseCard({ card, onClick }) {
  const theme = useTheme();
  if (!card) return null;

  const items = Array.isArray(card.items) ? card.items : [];
  const address = card.address;
  const addressLine = address
    ? [address.street, address.city && address.uf ? `${address.city}, ${address.uf}` : address.city || address.uf]
        .filter(Boolean)
        .join(" — ")
    : "";
  const userLabel = card.user?.name || "Sem usuário";

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2.5,
        cursor: onClick ? "pointer" : "default",
        bgcolor: "background.paper",
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        ...(onClick
          ? {
              "&:hover": {
                borderColor: "primary.light",
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
            }
          : {}),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.25}>
        <Stack spacing={0.25}>
          {card.orderCode && (
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              #{card.orderCode.replace(/^COD-/, "")}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption">{card.createdAtLabel || "—"}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
          {getStatusChip(card.status, theme)}
        </Stack>
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.3 }}>
        {card.companyName || "Cliente"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Responsável: <strong>{userLabel}</strong>
      </Typography>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Chip
          size="small"
          icon={getChannelIcon(card.channelType)}
          label={card.channelType || "Web"}
          variant="outlined"
        />
        {card.typePath ? (
          <Chip size="small" label={card.typePath} variant="outlined" sx={{ maxWidth: 280 }} />
        ) : null}
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 1.25 }}>
        <InfoPill label="Subtotal" value={money(card.subtotal)} />
        <InfoPill label="Frete" value={money(card.shippingCost)} />
        <InfoPill label="Total" value={money(card.total)} highlight />
      </Stack>

      {items.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0.75} sx={{ mb: 1 }}>
            {items.slice(0, 3).map((it, idx) => (
              <InfoRow key={idx} icon={<ShoppingCartRoundedIcon sx={{ fontSize: 18 }} />}>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  <strong>{it.qty}×</strong> {it.name || "Produto"}
                  {it.sku ? (
                    <Typography component="span" variant="caption" color="text.secondary" display="block">
                      SKU: {it.sku}
                    </Typography>
                  ) : null}
                </Typography>
              </InfoRow>
            ))}
            {items.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                + {items.length - 3} produto(s)
              </Typography>
            )}
          </Stack>
        </>
      )}

      {(card.payment || card.shipping) && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0.75} sx={{ mb: addressLine ? 1 : 0 }}>
            {card.payment && (
              <InfoRow icon={<PaymentsRoundedIcon sx={{ fontSize: 18, opacity: 0.75 }} />}>
                <Typography variant="body2">
                  {card.payment.methodLabel || "Pagamento"}
                  {card.payment.statusLabel ? ` · ${card.payment.statusLabel}` : ""}
                </Typography>
              </InfoRow>
            )}
            {card.shipping && (
              <InfoRow icon={<LocalShippingRoundedIcon sx={{ fontSize: 18, opacity: 0.75 }} />}>
                <Typography variant="body2">{card.shipping.methodLabel || "Envio"}</Typography>
              </InfoRow>
            )}
          </Stack>
        </>
      )}

      {addressLine && (
        <>
          <Divider sx={{ mb: 1 }} />
          <InfoRow icon={<RoomRoundedIcon sx={{ fontSize: 18 }} />}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {addressLine}
            </Typography>
          </InfoRow>
        </>
      )}
    </Paper>
  );
}
